// Ditado (Pedro, 31/10/2026, para o robozinho da Central: «igual esse que eu estou usando agora aqui no Claude»): o texto
// vai aparecendo enquanto a pessoa fala, já com vírgula, ponto e interrogação. O navegador manda o microfone direto para
// a transcrição ao vivo da OpenAI (gpt-live-transcribe) com um token curto que a API gera (POST
// /api/admin/atendimento/ditado); a chave nunca chega aqui.
//
// O modelo não tem detecção de fala do lado do servidor: cada trecho é fechado (commit) aqui, na pausa e no parar. As
// palavras chegam em deltas por trecho (item_id); o trecho fechado volta inteiro e substitui os deltas dele.
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { customFetch } from "@workspace/api-client-react";

export type EstadoDoDitado = "parado" | "ligando" | "ouvindo" | "terminando";

const TAXA = 24_000;
const PEDACO = TAXA / 10; // 100 ms por envio
const LIMIAR_DE_VOZ = 0.012; // RMS: acima disso é fala
const PAUSA_QUE_FECHA_MS = 1_200;
// Silêncio mandado antes do último commit: sem ele, a última palavra se perdia (teste de 31/10: «Obrigado» sumiu).
const SILENCIO_NO_FIM = new Float32Array(Math.round(TAXA * 0.6));
const ESPERA_DO_FIM_MS = 4_000;

const CAPTURA = `class CapturaDoDitado extends AudioWorkletProcessor {
  process(inputs) { const c = inputs[0] && inputs[0][0]; if (c && c.length) this.port.postMessage(c.slice(0)); return true; }
}
registerProcessor("captura-do-ditado", CapturaDoDitado);`;

function emBase64(f: Float32Array): string {
  const pcm = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i]!));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

function rms(f: Float32Array): number {
  let s = 0;
  for (let i = 0; i < f.length; i++) s += f[i]! * f[i]!;
  return Math.sqrt(s / Math.max(1, f.length));
}

function mensagemDoErro(err: unknown): string {
  const nome = (err as { name?: string } | null)?.name;
  if (nome === "NotAllowedError" || nome === "SecurityError") return "O navegador não liberou o microfone. Libere o microfone deste site e tente de novo.";
  if (nome === "NotFoundError") return "Nenhum microfone encontrado neste aparelho.";
  const api = (err as { data?: { error?: { message?: string } } } | null)?.data?.error?.message;
  return api || "Não foi possível ligar o ditado. Tente de novo.";
}

interface Sessao {
  ws: WebSocket;
  ctx: AudioContext;
  stream: MediaStream;
  no: AudioWorkletNode;
  acumulado: Float32Array[];
  amostras: number;
  /** Fala desde o último commit (pela energia do som ou por delta que já chegou). */
  falou: boolean;
  ultimaVoz: number;
  /** Trechos na ordem em que apareceram: o texto de cada um (deltas, ou o final). */
  trechos: Map<string, string>;
  /** Trechos fechados (commit) que ainda não voltaram completos. */
  pendentes: Set<string>;
  aguardandoCommit: number;
  fim: (() => void) | null;
}

/** O ditado cru: devolve o texto inteiro ditado a cada mudança. */
export function useDitado(onTexto: (ditado: string) => void, onErro: (erro: string | null) => void) {
  const [estado, setEstado] = useState<EstadoDoDitado>("parado");
  const sessao = useRef<Sessao | null>(null);
  const textoCb = useRef(onTexto);
  const erroCb = useRef(onErro);
  textoCb.current = onTexto;
  erroCb.current = onErro;

  const emitir = (s: Sessao) => {
    textoCb.current([...s.trechos.values()].map((t) => t.trim()).filter(Boolean).join(" "));
  };

  const soltarMicrofone = (s: Sessao) => {
    try { s.no.port.onmessage = null; s.no.disconnect(); } catch {}
    s.stream.getTracks().forEach((t) => t.stop());
    void s.ctx.close().catch(() => {});
  };

  const encerrar = useCallback((s: Sessao) => {
    if (sessao.current !== s) return;
    sessao.current = null;
    soltarMicrofone(s);
    s.ws.onclose = null;
    s.ws.onmessage = null;
    try { s.ws.close(); } catch {}
    s.fim?.();
    setEstado("parado");
  }, []);

  const fecharTrecho = (s: Sessao, comSilencio: boolean) => {
    if (s.ws.readyState !== WebSocket.OPEN) return;
    if (s.amostras > 0) {
      const junto = new Float32Array(s.amostras);
      let o = 0;
      for (const p of s.acumulado) { junto.set(p, o); o += p.length; }
      s.ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: emBase64(junto) }));
      s.acumulado = [];
      s.amostras = 0;
    }
    if (comSilencio) s.ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: emBase64(SILENCIO_NO_FIM) }));
    s.ws.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    s.aguardandoCommit++;
    s.falou = false;
  };

  const talvezTerminou = (s: Sessao) => {
    if (s.fim && s.aguardandoCommit <= 0 && s.pendentes.size === 0) encerrar(s);
  };

  const ligar = useCallback(async () => {
    if (sessao.current) return;
    erroCb.current(null);
    setEstado("ligando");
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let ws: WebSocket | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      const { token } = await customFetch<{ token: string }>("/api/admin/atendimento/ditado", { method: "POST" });
      ctx = new AudioContext({ sampleRate: TAXA });
      const url = URL.createObjectURL(new Blob([CAPTURA], { type: "application/javascript" }));
      try { await ctx.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
      ws = new WebSocket("wss://api.openai.com/v1/realtime?intent=transcription", ["realtime", `openai-insecure-api-key.${token}`]);
      await new Promise<void>((ok, falhou) => {
        const t = setTimeout(() => falhou(new Error("tempo")), 10_000);
        ws!.onopen = () => { clearTimeout(t); ok(); };
        ws!.onerror = () => { clearTimeout(t); falhou(new Error("conexão")); };
      });
      const no = new AudioWorkletNode(ctx, "captura-do-ditado");
      const s: Sessao = { ws, ctx, stream, no, acumulado: [], amostras: 0, falou: false, ultimaVoz: 0, trechos: new Map(), pendentes: new Set(), aguardandoCommit: 0, fim: null };
      sessao.current = s;
      ws.onmessage = (ev) => {
        let m: { type: string; item_id?: string; delta?: string; transcript?: string; error?: { message?: string; code?: string } };
        try { m = JSON.parse(String(ev.data)); } catch { return; }
        if (m.type === "conversation.item.input_audio_transcription.delta" && m.item_id) {
          s.trechos.set(m.item_id, (s.trechos.get(m.item_id) ?? "") + (m.delta ?? ""));
          // Delta de trecho já fechado (chega depois do commit) não conta como fala nova.
          if (s.aguardandoCommit === 0 && !s.pendentes.has(m.item_id)) s.falou = true;
          emitir(s);
        } else if (m.type === "input_audio_buffer.committed" && m.item_id) {
          s.aguardandoCommit--;
          if (!s.trechos.has(m.item_id)) s.trechos.set(m.item_id, "");
          s.pendentes.add(m.item_id);
        } else if (m.type === "conversation.item.input_audio_transcription.completed" && m.item_id) {
          s.trechos.set(m.item_id, m.transcript ?? s.trechos.get(m.item_id) ?? "");
          s.pendentes.delete(m.item_id);
          emitir(s);
          talvezTerminou(s);
        } else if (m.type === "conversation.item.input_audio_transcription.failed" && m.item_id) {
          s.pendentes.delete(m.item_id);
          talvezTerminou(s);
        } else if (m.type === "error") {
          // Commit sem áudio bastante (pausa logo depois de fechar um trecho): não é falha do ditado.
          if (/buffer/i.test(`${m.error?.code ?? ""} ${m.error?.message ?? ""}`)) {
            s.aguardandoCommit = Math.max(0, s.aguardandoCommit - 1);
            talvezTerminou(s);
            return;
          }
          erroCb.current("O ditado parou com um erro. O que já foi ditado ficou no campo.");
          encerrar(s);
        }
      };
      ws.onclose = () => {
        if (sessao.current !== s) return;
        if (!s.fim) erroCb.current("O ditado caiu. O que já foi ditado ficou no campo.");
        encerrar(s);
      };
      no.port.onmessage = (ev: MessageEvent<Float32Array>) => {
        if (s.fim) return;
        const f = ev.data;
        s.acumulado.push(f);
        s.amostras += f.length;
        const agora = performance.now();
        if (rms(f) > LIMIAR_DE_VOZ) { s.falou = true; s.ultimaVoz = agora; }
        if (s.amostras >= PEDACO) {
          const junto = new Float32Array(s.amostras);
          let o = 0;
          for (const p of s.acumulado) { junto.set(p, o); o += p.length; }
          s.acumulado = [];
          s.amostras = 0;
          if (ws!.readyState === WebSocket.OPEN) ws!.send(JSON.stringify({ type: "input_audio_buffer.append", audio: emBase64(junto) }));
        }
        if (s.falou && s.ultimaVoz > 0 && agora - s.ultimaVoz > PAUSA_QUE_FECHA_MS) fecharTrecho(s, false);
      };
      // O nó só processa ligado ao grafo: sai num ganho zero para não tocar o próprio microfone.
      const mudo = ctx.createGain();
      mudo.gain.value = 0;
      ctx.createMediaStreamSource(stream).connect(no);
      no.connect(mudo).connect(ctx.destination);
      setEstado("ouvindo");
    } catch (err) {
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => {});
      try { ws?.close(); } catch {}
      sessao.current = null;
      erroCb.current(mensagemDoErro(err));
      setEstado("parado");
    }
  }, [encerrar]);

  /** Para de ouvir e espera o último trecho voltar completo (até 4 s). */
  const parar = useCallback(() => {
    const s = sessao.current;
    if (!s) return Promise.resolve();
    if (s.fim) return new Promise<void>((ok) => { const antes = s.fim!; s.fim = () => { antes(); ok(); }; });
    setEstado("terminando");
    return new Promise<void>((ok) => {
      s.fim = ok;
      if (s.falou) fecharTrecho(s, true);
      soltarMicrofone(s);
      setTimeout(() => encerrar(s), ESPERA_DO_FIM_MS);
      talvezTerminou(s);
    });
  }, [encerrar]);

  // Sair da tela (fechar o diálogo, trocar de conversa) desliga o microfone na hora.
  useEffect(() => () => { if (sessao.current) encerrar(sessao.current); }, [encerrar]);

  return { estado, ligar, parar };
}

/** O ditado num campo de texto: o que já estava escrito fica, e o ditado entra depois dele. */
export function useDitadoNoCampo(texto: string, setTexto: (t: string) => void, campo: RefObject<HTMLTextAreaElement | null>, onErro: (erro: string | null) => void) {
  const base = useRef("");
  const ditado = useDitado((d) => {
    setTexto(base.current + d);
    requestAnimationFrame(() => {
      const el = campo.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, onErro);
  const alternar = () => {
    if (ditado.estado === "ouvindo") {
      void ditado.parar().then(() => campo.current?.focus());
    } else if (ditado.estado === "parado") {
      base.current = texto.trim() ? `${texto.trimEnd()} ` : "";
      void ditado.ligar();
    }
  };
  return { estado: ditado.estado, alternar, ativo: ditado.estado !== "parado" };
}
